/**
 * Shared voice-reference readiness rules.
 * Source of truth mirrors backend `voice_reference_quality_policy.py` +
 * frontend `voiceReferenceQuality.ts` / `buildQualityAnalysis`.
 */

import {
  EFFECTIVE_MAX_DURATION_SECONDS,
  EFFECTIVE_MIN_DURATION_SECONDS,
  PRODUCT_RECOMMENDED_MAX_SECONDS,
  PRODUCT_RECOMMENDED_MIN_SECONDS,
  type VoiceReferenceQualityAnalysis,
} from './voiceReferenceQualityPolicy'

/** Matches `buildQualityAnalysis` no_speech threshold (meanRms). */
export const HARD_MIN_MEAN_RMS = 0.01
/** Matches `buildQualityAnalysis` volume_low warning floor. */
export const WARN_LOW_MEAN_RMS = 0.02
/** Matches `buildQualityAnalysis` too_much_silence threshold. */
export const HARD_MAX_SILENCE_RATIO = 0.85

export type ReadinessRuleKind = 'hard' | 'recommended'
export type ReadinessItemStatus = 'idle' | 'pending' | 'pass' | 'fail'

export type RecordingReadinessRuleId = 'min_duration' | 'speech_presence' | 'silence_ratio' | 'recommended_duration'

export type RecordingReadinessRule = {
  id: RecordingReadinessRuleId
  kind: ReadinessRuleKind
  sourceOfTruth: string
  /** Product threshold in seconds where applicable. */
  thresholdSeconds?: number
}

export const RECORDING_READINESS_RULES: readonly RecordingReadinessRule[] = [
  {
    id: 'min_duration',
    kind: 'hard',
    sourceOfTruth: 'EFFECTIVE_MIN_DURATION_SECONDS (product 20s ∩ provider min)',
    thresholdSeconds: EFFECTIVE_MIN_DURATION_SECONDS,
  },
  {
    id: 'speech_presence',
    kind: 'hard',
    sourceOfTruth: 'buildQualityAnalysis meanRms >= 0.01 / backend mean_volume_db > -50',
  },
  {
    id: 'silence_ratio',
    kind: 'hard',
    sourceOfTruth: 'buildQualityAnalysis silenceRatio <= 0.85 when duration >= min',
  },
  {
    id: 'recommended_duration',
    kind: 'recommended',
    sourceOfTruth: 'PRODUCT_RECOMMENDED_MIN_SECONDS (30–60s product guidance)',
    thresholdSeconds: PRODUCT_RECOMMENDED_MIN_SECONDS,
  },
] as const

export type LiveAudioStats = {
  meanRms: number
  peak: number
  silenceRatio: number
  speechSeconds: number
  frameCount: number
}

export type RecordingReadinessItem = {
  id: RecordingReadinessRuleId
  kind: ReadinessRuleKind
  status: ReadinessItemStatus
  currentSeconds?: number
  targetSeconds?: number
  detail?: string
}

export type RecordingReadinessSnapshot = {
  items: RecordingReadinessItem[]
  hardPass: boolean
  allHardKnown: boolean
  recommendedPass: boolean
}

export type ReadinessEvalMode = 'idle' | 'live' | 'final'

export function emptyLiveAudioStats(): LiveAudioStats {
  return { meanRms: 0, peak: 0, silenceRatio: 1, speechSeconds: 0, frameCount: 0 }
}

export function evaluateRecordingReadiness(args: {
  mode: ReadinessEvalMode
  durationMs: number
  live?: LiveAudioStats | null
  analysis?: VoiceReferenceQualityAnalysis | null
}): RecordingReadinessSnapshot {
  const durationSec = Math.max(0, args.durationMs / 1000)
  const meanRms = args.analysis?.meanRms ?? args.live?.meanRms ?? 0
  const silenceRatio = args.analysis?.silenceRatio ?? args.live?.silenceRatio ?? 1
  const speechSeconds = args.analysis?.speechSeconds ?? args.live?.speechSeconds ?? 0

  const items: RecordingReadinessItem[] = RECORDING_READINESS_RULES.map((rule) => {
    if (args.mode === 'idle') {
      return {
        id: rule.id,
        kind: rule.kind,
        status: 'idle',
        currentSeconds: 0,
        targetSeconds: rule.thresholdSeconds,
      }
    }

    if (rule.id === 'min_duration') {
      const pass = durationSec >= EFFECTIVE_MIN_DURATION_SECONDS
      return {
        id: rule.id,
        kind: rule.kind,
        status: pass ? 'pass' : 'pending',
        currentSeconds: durationSec,
        targetSeconds: EFFECTIVE_MIN_DURATION_SECONDS,
      }
    }

    if (rule.id === 'recommended_duration') {
      const pass = durationSec >= PRODUCT_RECOMMENDED_MIN_SECONDS
      return {
        id: rule.id,
        kind: rule.kind,
        status: pass ? 'pass' : args.mode === 'live' || durationSec > 0 ? 'pending' : 'idle',
        currentSeconds: durationSec,
        targetSeconds: PRODUCT_RECOMMENDED_MIN_SECONDS,
      }
    }

    if (rule.id === 'speech_presence') {
      // Final analysis may set no_speech / too_much_silence blocking.
      if (args.analysis) {
        const blocked =
          args.analysis.blockingReasons.includes('no_speech') ||
          args.analysis.blockingReasons.includes('too_much_silence')
        const pass = !blocked && meanRms >= HARD_MIN_MEAN_RMS
        return {
          id: rule.id,
          kind: rule.kind,
          status: pass ? 'pass' : 'fail',
          currentSeconds: speechSeconds,
        }
      }
      // Analyzing after stop: wait for decode — do not false-fail on empty live stats.
      if (args.mode === 'final' && !args.live) {
        return {
          id: rule.id,
          kind: rule.kind,
          status: 'pending',
          currentSeconds: speechSeconds,
        }
      }
      // Live: require detectable voiced energy (same RMS floor) with some speech time.
      const pass = meanRms >= HARD_MIN_MEAN_RMS && speechSeconds >= Math.min(2, EFFECTIVE_MIN_DURATION_SECONDS * 0.15)
      return {
        id: rule.id,
        kind: rule.kind,
        status: pass ? 'pass' : 'pending',
        currentSeconds: speechSeconds,
      }
    }

    if (rule.id === 'silence_ratio') {
      if (args.mode === 'final' && !args.analysis && !args.live) {
        return {
          id: rule.id,
          kind: rule.kind,
          status: 'pending',
          detail: undefined,
        }
      }
      if (durationSec < EFFECTIVE_MIN_DURATION_SECONDS) {
        // Not yet evaluable as a hard fail — keep pending while recording.
        return {
          id: rule.id,
          kind: rule.kind,
          status: args.mode === 'final' && args.analysis ? (silenceRatio > HARD_MAX_SILENCE_RATIO ? 'fail' : 'pass') : 'pending',
          detail: `${Math.round(silenceRatio * 100)}%`,
        }
      }
      if (args.analysis?.blockingReasons.includes('too_much_silence')) {
        return { id: rule.id, kind: rule.kind, status: 'fail', detail: `${Math.round(silenceRatio * 100)}%` }
      }
      const pass = silenceRatio <= HARD_MAX_SILENCE_RATIO
      return {
        id: rule.id,
        kind: rule.kind,
        status: pass ? 'pass' : args.mode === 'final' ? 'fail' : 'pending',
        detail: `${Math.round(silenceRatio * 100)}%`,
      }
    }

    return { id: rule.id, kind: rule.kind, status: 'idle' }
  })

  const hardItems = items.filter((i) => i.kind === 'hard')
  const hardPass = hardItems.every((i) => i.status === 'pass')
  const allHardKnown = hardItems.every((i) => i.status === 'pass' || i.status === 'fail')
  const recommendedPass = items.filter((i) => i.kind === 'recommended').every((i) => i.status === 'pass')

  return { items, hardPass, allHardKnown, recommendedPass }
}

export function canUseVoiceReferenceSample(analysis: VoiceReferenceQualityAnalysis | null): boolean {
  if (!analysis) return false
  if (!analysis.captureOk || !analysis.decodeOk) return false
  return analysis.status !== 'BLOCKED'
}

export {
  EFFECTIVE_MIN_DURATION_SECONDS,
  EFFECTIVE_MAX_DURATION_SECONDS,
  PRODUCT_RECOMMENDED_MIN_SECONDS,
  PRODUCT_RECOMMENDED_MAX_SECONDS,
}
